import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../../../../../models/hooks/appStateHooks';
import { useContactCustomFields, useCreateContactCustomPropertyMutation } from '../../../../../../../queries';
import { EditItemPenIcon } from '../../../../../../components/svgs/icons/EditItemPenIcon';
import { TextInput } from '../../../../../../components/TextInput';
import { styleSheet } from '../styles';

const isValidUrl = (str: string) => {
	try {
		new URL(str);
		return true;
	} catch {
		return false;
	}
};

const getInitialValue = (customField: Api.IFormField<string>, contact: Api.IContact) => {
	if (!contact.customProperties) {
		return '';
	}
	return contact.customProperties[customField.name] ?? '';
};

const CustomField = ({
	customField,
	contact,
	onValueSaved,
}: {
	customField: Api.IFormField<string>;
	contact: Api.IContact;
	onValueSaved?: () => void;
}) => {
	const [value, setValue] = React.useState<string>(getInitialValue(customField, contact));
	const [initialValue] = React.useState<string>(value);
	const [isEditing, setIsEditing] = React.useState(false);

	const createContactCustomPropertyMutation = useCreateContactCustomPropertyMutation({
		onSuccess: () => {
			setIsEditing(false);
			onValueSaved?.();
		},
	});

	// Sync local value when the contact's custom properties change, but never
	// while the user is actively editing — otherwise an in-flight tab-focus
	// refetch would clobber their unsaved input.
	React.useEffect(() => {
		if (isEditing) {
			return;
		}
		const newValue = getInitialValue(customField, contact);
		setValue(newValue);
	}, [customField, contact, isEditing]);

	const onEdit = () => {
		setIsEditing(true);
	};

	const onSave = () => {
		createContactCustomPropertyMutation.mutate({
			contactId: contact.id,
			fieldName: customField.name,
			value,
		});
	};

	const onCancel = () => {
		setValue(initialValue);
		setIsEditing(false);
	};

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			onSave();
		} else if (e.key === 'Escape') {
			onCancel();
		}
	};

	return (
		<div className={css(styleSheet.customFieldItem)}>
			<div className={css(styleSheet.customFieldLabel)}>{customField.label}</div>

			<div className={css(styleSheet.customFieldData)}>
				{isEditing ? (
					<div className={css(styleSheet.customFieldEditingForm)}>
						<TextInput
							inputId={`edit-custom-field-${customField.name}`}
							onChange={e => setValue(e.target.value)}
							onKeyDown={onKeyDown}
							type='text'
							value={value}
							autoFocus
						/>
						<div className={css(styleSheet.customFieldActionsRow)}>
							<button
								className={css(styleSheet.customFieldSaveButton)}
								onClick={onSave}
								disabled={createContactCustomPropertyMutation.isPending}
							>
								Save
							</button>
							<button className={css(styleSheet.customFieldCancelButton)} onClick={onCancel}>
								Cancel
							</button>
						</div>
					</div>
				) : (
					<div className={css(styleSheet.customFieldValueDisplay)}>
						<div className={css(styleSheet.customFieldValueContent)}>
							{value !== '' ? (
								<span title={value} className={css(styleSheet.customFieldValue)}>
									{isValidUrl(value) ? (
										<a href={value} target='_blank' rel='noopener noreferrer' className='brand-link'>
											{value}
										</a>
									) : (
										value
									)}
								</span>
							) : (
								<span className={css(styleSheet.customFieldPlaceholder)}>Click to add value</span>
							)}
						</div>

						<button onClick={onEdit} className={css(styleSheet.customFieldEditButton)}>
							<EditItemPenIcon />
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

type Props = {
	contact: Api.IContact;
	onCustomFieldSaved?: () => void;
};

export const CustomFields = ({ contact, onCustomFieldSaved }: Props) => {
	const userSession = useUserSession();

	const hasCustomContactFields = userSession.account.additionalInfo?.hasCustomContactFields;

	const customFieldsQuery = useContactCustomFields({
		refetchOnWindowFocus: false,
		enabled: hasCustomContactFields,
	});

	const customFields = customFieldsQuery.data?.fields;

	const handleValueSaved = () => {
		onCustomFieldSaved?.();
	};

	if (!hasCustomContactFields || !customFields || customFields.length === 0) {
		return null;
	}

	return (
		<div className={css(styleSheet.customFieldsContainer)}>
			<div className={css(styleSheet.customFieldsContent)}>
				{customFields.map(customField => (
					<CustomField
						key={`custom-field-${customField.name}`}
						customField={customField}
						contact={contact}
						onValueSaved={handleValueSaved}
					/>
				))}
			</div>
		</div>
	);
};
